/**
 * RecentlyViewed (E20-1550)
 *
 * Fills the Recently Viewed row with house Product Cards. The card markup is Liquid — this
 * element only decides WHICH products to show and asks the section to render them, so no card
 * HTML is assembled here. See sections/bfx-recently-viewed.liquid for the render contract.
 *
 * Replaces Impulse's theme.RecentlyViewed without editing assets/theme.js: that module only
 * claims containers carrying [data-section-type="recently-viewed"], and this section doesn't
 * emit one. theme.js still RECORDS visits — addIdToRecentlyViewed writes the `theme-recent`
 * key on every PDP — so the history itself keeps working untouched, and this reads the same key.
 *
 * Behaviour matches the stock row so the swap is invisible to shoppers: localStorage holds
 * handles oldest-first (a re-visit is deleted and re-appended), so the list is reversed to put
 * the newest first, and the product currently being viewed is left out of its own row.
 */

if (!customElements.get('recently-viewed')) {
  class RecentlyViewed extends HTMLElement {
    // Impulse's key — shared on purpose so history recorded by theme.js still counts.
    static STORAGE_KEY = 'theme-recent';

    // Liquid resolves at most 20 products per page through all_products, so never ask for more
    // than that however much history the browser is holding.
    static MAX_HANDLES = 20;

    // Reserved first term marking a request as ours. It rides inside `q` because that is the only
    // caller-supplied value Liquid can read, and the section renders cards ONLY when it sees this
    // — a shopper's query on the search results page also fills search.terms, so terms alone
    // can't tell the two apart. Keep in sync with `sentinel` in bfx-recently-viewed.liquid.
    static SENTINEL = '__bfxrv__';

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.maxProducts = Number(this.dataset.maxProducts) || 5;
      this.currentHandle = this.dataset.currentHandle || '';

      this.fill();
    }

    /** Visited handles, newest first, minus the product being viewed right now. */
    get handles() {
      let stored;

      try {
        stored = JSON.parse(localStorage.getItem(RecentlyViewed.STORAGE_KEY)) || {};
      } catch (error) {
        // Unreadable or corrupt history is the same as no history: the row stays hidden.
        console.warn('[recently-viewed] could not read stored history', error);
        return [];
      }

      return (
        Object.keys(stored)
          // 'undefined' is a real key Impulse can write when a PDP renders without a handle.
          .filter((handle) => handle && handle !== 'undefined' && handle !== this.currentHandle)
          .reverse()
          // Send as many handles as Liquid can resolve, not a multiple of the row length, so
          // stale or unavailable history can't shorten the row. Liquid stops rendering once it
          // has the configured number of cards, so asking for more costs nothing — it's still
          // one request, and the extras are only consulted when an earlier handle drops out.
          // A 2x slice looked sufficient but isn't: with a maximum of 2, three unavailable
          // newest products would make it ignore a perfectly good fifth handle.
          .slice(0, RecentlyViewed.MAX_HANDLES)
      );
    }

    /**
     * Request the row from the section itself. The handles ride along as the search query
     * because Liquid can't read arbitrary query params, and `search.terms` is the only route
     * that hands a caller-supplied list to a section — one request for the whole row.
     */
    async fill() {
      // connectedCallback runs again every time the element is detached and reinserted — which
      // the theme editor does when a section is moved. Without these two guards a reconnect
      // starts a second fetch and both responses append a row, so the shopper sees the carousel
      // twice. Bailing on an already-appended row covers the settled case; the in-flight flag
      // covers a reconnect while the first request is still open. A failed request appends
      // nothing and clears the flag, so a later reconnect still retries.
      if (this.loading || this.querySelector('[data-recently-viewed-row]')) return;

      const handles = this.handles;
      if (!handles.length) return;

      this.loading = true;

      const params = new URLSearchParams({
        section_id: this.sectionId,
        type: 'product',
        q: [RecentlyViewed.SENTINEL, ...handles].join(' '),
      });

      // routes.root carries the locale prefix, so the fetch stays inside /fr on French CA.
      const url = `${window.Shopify.routes.root}search?${params}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

        const markup = await response.text();
        const row = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-recently-viewed-row]');

        // No row means every stored product has since been deleted, discontinued, or is
        // unavailable in this market — leave the section hidden rather than show an empty shell.
        if (!row) return;

        // Reveal BEFORE appending. <bfx-carousel> measures the track in connectedCallback and
        // has no ResizeObserver to recompute, so connecting it inside a display:none host makes
        // it read zero-width geometry and disable both arrows permanently. Measured at 900px
        // with 4 cards: track scrollWidth 1192 vs clientWidth 900 — genuinely scrollable — and
        // the next arrow still shipped disabled until the carousel reconnected while visible.
        // Both statements run in the same task, so there's no painted frame of an empty row.
        this.hidden = false;

        // Append the row whole: <bfx-carousel> also reads its cells in connectedCallback, so it
        // has to connect with them already in place or the arrows have nothing to page through.
        this.appendChild(row);
      } catch (error) {
        console.warn('[recently-viewed] could not load the row', error);
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define('recently-viewed', RecentlyViewed);
}
